import { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { type JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { History, RotateCcw, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';

/**
 * Assumed API surface (documented contract only guarantees getNode/updateNode/uploadImage):
 *   - api.getVersions(nodeId)                 -> VersionMeta[]   (ASSUMED)
 *   - api.getVersion(nodeId, versionNum)      -> { body }        (ASSUMED — only used if
 *       the list items don't already carry a body, e.g. GET /api/nodes/{id}/versions/{n})
 *   - api.restoreVersion(nodeId, versionNum)  -> node            (ASSUMED)
 * All three are called defensively: if a method is missing or the request fails, the panel
 * degrades to "No history yet" / metadata-only preview / a disabled Restore rather than crashing.
 */
interface VersionMeta {
  versionNum: number;
  title: string;
  editedBy?: string | null;
  editedAt?: string | null;
  /** The server snapshot happens to include `body`; use it directly when present. */
  body?: JSONContent | null;
}

interface WikiVersionsApi {
  getVersions?: (nodeId: string) => Promise<VersionMeta[]>;
  getVersion?: (nodeId: string, versionNum: number) => Promise<{ body?: JSONContent | null }>;
  restoreVersion?: (nodeId: string, versionNum: number) => Promise<unknown>;
}

export interface VersionHistoryProps {
  nodeId: string;
  onClose: () => void;
  /** Called after a successful restore so the parent can reload the live node. */
  onRestored?: () => void;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function VersionHistory({ nodeId, onClose, onRestored }: VersionHistoryProps) {
  const client = api as unknown as WikiVersionsApi;

  const [versions, setVersions] = useState<VersionMeta[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [previewBody, setPreviewBody] = useState<JSONContent | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // ---- Load the version list ----
  useEffect(() => {
    let alive = true;
    (async () => {
      setVersions(null);
      setLoadError(null);
      if (typeof client.getVersions !== 'function') {
        if (alive) setVersions([]); // endpoint not wired up yet → "no history yet"
        return;
      }
      try {
        const list = await client.getVersions(nodeId);
        if (!alive) return;
        const sorted = [...(list ?? [])].sort((a, b) => b.versionNum - a.versionNum);
        setVersions(sorted);
        if (sorted.length) selectVersion(sorted[0]);
      } catch (e) {
        if (alive) setLoadError(e instanceof Error ? e.message : 'Failed to load history');
        if (alive) setVersions([]);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // ---- Select + preview a version ----
  async function selectVersion(v: VersionMeta) {
    setSelected(v.versionNum);
    if (v.body) {
      setPreviewBody(v.body);
      return;
    }
    // Fall back to a per-version fetch if the list didn't carry the body.
    if (typeof client.getVersion === 'function') {
      setPreviewLoading(true);
      try {
        const full = await client.getVersion(nodeId, v.versionNum);
        setPreviewBody(full?.body ?? null);
      } catch {
        setPreviewBody(null);
      } finally {
        setPreviewLoading(false);
      }
    } else {
      setPreviewBody(null);
    }
  }

  async function handleRestore() {
    if (selected == null || typeof client.restoreVersion !== 'function') return;
    setRestoring(true);
    try {
      await client.restoreVersion(nodeId, selected);
      onRestored?.();
      onClose();
    } catch {
      // Keep the panel open; surfacing failure inline avoids a destructive silent loss.
      setLoadError('Restore failed — please try again.');
    } finally {
      setRestoring(false);
    }
  }

  const canRestore = typeof client.restoreVersion === 'function' && selected != null;
  const selectedMeta = useMemo(
    () => versions?.find((v) => v.versionNum === selected) ?? null,
    [versions, selected],
  );

  return (
    <aside className="flex h-full w-full max-w-sm flex-col border-l border-border bg-surface">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          <History size={16} className="text-ink-muted" />
          Version history
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close version history"
          className="btn btn-ghost h-7 w-7 p-0"
        >
          <X size={16} />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">
        {/* List */}
        <div className="max-h-56 shrink-0 overflow-y-auto border-b border-border">
          {versions === null ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-ink-faint">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : versions.length === 0 ? (
            <div className="px-4 py-6 text-sm text-ink-faint">
              No history yet.
              {loadError && <span className="mt-1 block text-ink-faint">({loadError})</span>}
            </div>
          ) : (
            <ul className="py-1">
              {versions.map((v) => (
                <li key={v.versionNum}>
                  <button
                    type="button"
                    onClick={() => selectVersion(v)}
                    className={clsx(
                      'flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left transition-colors',
                      selected === v.versionNum ? 'bg-surface-3' : 'hover:bg-surface-2',
                    )}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <span className="truncate text-sm text-ink">
                        v{v.versionNum} · {v.title || 'Untitled'}
                      </span>
                    </span>
                    <span className="text-xs text-ink-faint">
                      {v.editedBy ? `${v.editedBy} · ` : ''}
                      {fmtDate(v.editedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Preview */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {selected == null ? (
            <p className="text-sm text-ink-faint">Select a version to preview it.</p>
          ) : previewLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-faint">
              <Loader2 size={14} className="animate-spin" /> Loading preview…
            </div>
          ) : previewBody ? (
            <ReadOnlyPreview content={previewBody} />
          ) : (
            <div className="text-sm text-ink-faint">
              <p className="font-medium text-ink">{selectedMeta?.title || 'Untitled'}</p>
              <p className="mt-1">Preview unavailable for this version.</p>
            </div>
          )}
        </div>
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
        <span className="text-xs text-ink-faint">
          {selected != null ? `Viewing v${selected}` : ' '}
        </span>
        <button
          type="button"
          onClick={handleRestore}
          disabled={!canRestore || restoring}
          className="btn btn-outline"
          title={
            typeof client.restoreVersion === 'function'
              ? 'Restore this version'
              : 'Restore endpoint not available'
          }
        >
          {restoring ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          Restore
        </button>
      </footer>
    </aside>
  );
}

/* ------------------------- read-only preview ------------------------- */

function ReadOnlyPreview({ content }: { content: JSONContent }) {
  const editor = useEditor(
    {
      editable: false,
      extensions: [StarterKit, Image],
      content,
      editorProps: { attributes: { class: 'tiptap max-w-none text-sm' } },
    },
    [],
  );

  useEffect(() => {
    if (editor) editor.commands.setContent(content, false);
  }, [editor, content]);

  if (!editor) return null;
  return <EditorContent editor={editor} />;
}
